<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h4 qid="Framework-view-page-title" class="mb-3">
              {{ framework.name }}
            </h4>
            <div class="mb-2">
              <span v-if="getGuide('framework')">
                <GuideButton :guide="getGuide('framework')"></GuideButton>
              </span>
            </div>
          </div>

          <div class="col-md-6 text-right" qid="view-Framework-modified">
            <div class="mb-4">
                <span class="h6 ml-3">
                <span v-if="isGranted([permissions.FRAMEWORK_EDIT, permissions.FRAMEWORK_EDIT_OWN], framework.created_by) && (!getClient.parent || (getClient.parent && framework.partition))"
                      class="btn btn-edit"
                      qid="edit-Framework-button"
                      @click="showEditFrameworkModal(framework)"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span v-if="isGranted([permissions.FRAMEWORK_DELETE, permissions.FRAMEWORK_DELETE_OWN], framework.created_by) && (!getClient.parent || (getClient.parent && framework.partition))"
                      class="btn btn-delete"
                      qid="delete-Framework-button"
                      @click="showConfirmDeleteModal(framework)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </span>
            </div>
            <div>
              <span>{{ $t('table.last_updated') }}</span>
              <span class="ml-2 text-muted small" qid="view-Framework-modified-date">
              {{ framework.updated_at | formatDate }}
            </span>
              <Avatar v-if="framework.updated_by" :object="framework.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
              <span v-if="framework.updated_by" qid="view-Framework-modified-by">
              {{ framework.updated_by.name }}
            </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-tabs class="mt-3" v-model="tabIndex">

      <!-- First tab -->
      <b-tab :title="$t('maintenance.frameworks.tab.basic_info')" active>
        <br>
        <div class="card">
          <div class="card-body">
            <div class="content-view-body">
              <div class="mb-2">
                <h5 class="h5" v-if="!showOnTask">
                  {{ $t('maintenance.frameworks.create.description_label') }}
                </h5>
              </div>
              <div v-if="!showDescriptionInput" class="section-container">
                <div v-if="framework.description && framework.description.length > descriptionLength">
                  <p v-if="showFullDescription" class="text-formatted">{{ framework.description }}</p>
                  <p v-if="!showFullDescription" class="text-formatted">{{ framework.description | truncate(descriptionLength) }}</p>
                  <div class="text-center mt-3" @click="showFullDescription=!showFullDescription">
                    <span class="cursor-pointer" v-if="!showFullDescription">
                      <span class="mr-2">
                        {{ $t('system.view_more') }}
                      </span>
                      <font-awesome-icon icon="chevron-down"/>
                    </span>
                    <span class="cursor-pointer" v-if="showFullDescription">
                      <span class="mr-2">
                        {{ $t('system.view_less') }}
                      </span>
                      <font-awesome-icon icon="chevron-up"/>
                    </span>
                  </div>
                </div>
                <p v-if="framework.description && framework.description.length <= descriptionLength"
                   class="text-formatted"
                >{{ framework.description }}</p>
                <p v-if="!framework.description" class="text-muted">
                  {{ $t('system.missing_input') }}
                </p>
              </div>
            </div>
            <div class="mt-3">
              <h6 class="h6">
                {{ $t('maintenance.third_parties.create.attachments') }}
                &nbsp;
                <span class="text-muted">({{ $t('attachments.file_size_label') }})</span>
              </h6>
              <hr>
              <b-alert v-if="error.length"
                       show
                       variant="danger"
                       qid="attachments-f-error"
              >
                {{ error }}
              </b-alert>
              <p v-for="file in framework.media">
                <button class="float-right btn btn-link btn-inline-icons"
                        @click="openDeleteAttachmentModal(file)"
                        qid="remove-media-item"
                        v-if="isGranted([permissions.FRAMEWORK_EDIT, permissions.FRAMEWORK_EDIT_OWN], framework.created_by) && (!getClient.parent || (getClient.parent && framework.partition))"
                >
                  <font-awesome-icon icon="trash-alt" />
                </button>
                <span class="mr-3 float-right text-muted cursor-pointer"
                      @click="downloadMedia(file)"
                      qid="download-media"
                >
                  <font-awesome-icon icon="cloud-download-alt" />
                </span>
                <a @click="downloadMedia(file)" qid="create-f-media-item">
                  {{ file.file_name }}
                </a>
              </p>
              <label v-if="isGranted([permissions.FRAMEWORK_EDIT, permissions.FRAMEWORK_EDIT_OWN], framework.created_by) && (!getClient.parent || (getClient.parent && framework.partition))"
                     class="btn btn-secondary cursor-pointer"
                     qid="create-f-file-button"
              >
                <input type="file"
                       name="fileupload"
                       value="fileupload"
                       multiple
                       id="file"
                       @change="onFileChange"
                       @click="resetFile"
                >
                <span>
                  <font-awesome-icon icon="cloud-upload-alt" />
                  {{ $t('system.upload') }}
                </span>
              </label>
              <br>
            </div>
          </div>
        </div>
        <br>
        <div class="card">
          <div class="card-body">
            <div class="content-view-body">
              <div class="mb-2">
                <h5 class="h5" v-if="!showOnTask">
                  {{ $t('checklists.framework.title') }}
                </h5>
                <br>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-6">
                <h6 class="h6" v-if="!showOnTask">
                  {{ $t('checklists.framework.type_1_title') }}
                </h6>
                <hr>
                <br>
                <div class="checklist-wrapper">
                  <ol class="checklist-step-progress">
                    <li v-for="(object, index) in checklistItems.slice(0, 12)"
                        class="checklist-step-progress-item"
                        :class="{
                          'not-started': object.value == 'Not started',
                          'skipped': object.value == 'Skipped',
                          'in-progress': object.value == 'In progress',
                          'completed': object.value == 'Completed'
                        }"
                    >
                      <a @click="showChecklistItemModal(object, index)" v-b-tooltip.topleft.hover :title="$t('checklists.status.' + $options.filters.slugify(object.value))">
                        {{ $t('checklists.framework.' + object.name + '.name') }}
                      </a>
                      <p v-if="object.value=='Not started' || object.value=='In progress'">{{ $t('checklists.framework.' + object.name + '.description') }}</p>
                    </li>
                  </ol>
                </div>
              </div>

              <div class="col-sm-6">
                <h6 class="h6" v-if="!showOnTask">
                  {{ $t('checklists.framework.type_2_title') }}
                </h6>
                <hr>
                <br>
                <div class="checklist-wrapper">
                  <ol class="checklist-step-progress">
                    <li v-for="(object, index) in checklistItems.slice(12, 25)"
                        class="checklist-step-progress-item"
                        :class="{
                          'not-started': object.value == 'Not started',
                          'skipped': object.value == 'Skipped',
                          'in-progress': object.value == 'In progress',
                          'completed': object.value == 'Completed'
                        }"
                    >
                      <a @click="showChecklistItemModal(object, index)" v-b-tooltip.topleft.hover :title="$t('checklists.status.' + $options.filters.slugify(object.value))">
                        {{ $t('checklists.framework.' + object.name + '.name') }}
                      </a>
                      <p v-if="object.value=='Not started' || object.value=='In progress'">{{ $t('checklists.framework.' + object.name + '.description') }}</p>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- Second tab -->
      <b-tab :title="$t('maintenance.frameworks.tab.framework_controls')" class="mt-3" @click="frameworkTabClicked = true">
        <FrameworkControlsTable :framework="framework" v-if="frameworkTabClicked"></FrameworkControlsTable>
      </b-tab>

      <!-- Third tab -->
      <b-tab :title="$t('maintenance.frameworks.tab.history')" class="mt-3" @click="historyTabClicked">
        <div v-if="showHistoryTab">
          <FrameworkHistory :object="framework"></FrameworkHistory>
        </div>
      </b-tab>
    </b-tabs>


    <!-- Edit Framework modal template -->
    <b-modal ref="edit-framework" @hide="hideEditFModal" modal-class="task-modal" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideEditFModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="update-f-title">
            {{ $t('maintenance.frameworks.update.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-f-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="editFFormSubmit" qid="edit-f-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-f-name-label">
              {{ $t('maintenance.frameworks.create.name_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-input
              qid="edit-f-form-name"
              type="text"
              v-model="fForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.frameworks.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-f-description-label">
              {{ $t('maintenance.frameworks.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-f-form-description"
              v-model="fForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('maintenance.frameworks.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
        </div>
        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-3"
                            variant="secondary"
                            @click="hideEditFModal"
                            qid="edit-f-cancel-button"
                  >
                    {{ $t('maintenance.frameworks.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="mt-3 ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="edit-f-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('maintenance.frameworks.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Edit Checklist Item modal template -->
    <b-modal ref="edit-checklist-item" @hide="hideChecklistItemModal" modal-class="task-modal" size="lg" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideChecklistItemModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="edit-checklist-item-title">
            {{ $t('checklists.update.title') }}
          </h5>

          <b-form-group>
            <div class="">
              <span class="btn btn-secondary btn-xs float-right cursor-pointer" @click="openGuide()">
                <font-awesome-icon icon="eye" />
                {{ $t('administration.guide.modal.button') }}
              </span>
              <label qid="create-p-users-label">
                {{ $t('system.help_center_label') }}
              </label>
              <div v-if="wpGuide && wpGuide.id" class="guide-content-container">
                <p v-if="!showFullGuideDescription" v-html="wpGuide.excerpt.rendered"></p>
                <p v-if="showFullGuideDescription" v-html="wpGuide.content.rendered"></p>

                <div class="text-center mt-3" @click="showFullGuideDescription=!showFullGuideDescription">
                  <span class="cursor-pointer" v-if="!showFullGuideDescription">
                    <span class="mr-2">
                      {{ $t('system.view_more') }}
                    </span>
                    <font-awesome-icon icon="chevron-down"/>
                  </span>
                  <span class="cursor-pointer" v-if="showFullGuideDescription">
                    <span class="mr-2">
                      {{ $t('system.view_less') }}
                    </span>
                    <font-awesome-icon icon="chevron-up"/>
                  </span>
                </div>
              </div>
            </div>
          </b-form-group>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-checklist-item-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="editChecklistSubmit(checklistForm)" qid="edit-checklist-item-form">
        <div class="row">
          <div class="col-sm-4" style="border-right: 1px solid #bdc3c7;">
            <div class="form-container">
              <b-form-group>
                <label qid="edit-checklist-item-name-label">
                  {{ $t('checklists.update.name_label') }}
                </label>
                <p>{{ $t('checklists.framework.' + checklistItem.name + '.name') }}</p>
              </b-form-group>
              <b-form-group>
                <label qid="edit-checklist-item-description-label">
                  {{ $t('checklists.update.description_label') }}
                </label>
                <p>{{ $t('checklists.framework.' + checklistItem.name + '.description') }}</p>
              </b-form-group>
              <b-form-group>
                <label qid="edit-checklist-item-status-label">
                  {{ $t('checklists.update.status_label') }}<span class="mandatory"> *</span>
                </label>
                <multiselect
                    v-model="checklistForm"
                    :options="statusItems"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    :placeholder="$t('checklists.update.status_placeholder')"
                    label="name"
                    required
                    :allow-empty="false"
                    qid="edit-checklist-item-status"
                >
                  <template slot="singleLabel" slot-scope="props">
                    <font-awesome-icon icon="exclamation-triangle" class="risk-0 mr-2" v-if="props.option.value == 'Not started'"/>
                    <font-awesome-icon icon="times-circle" class="icon-danger mr-2" v-if="props.option.value == 'Skipped'"/>
                    <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="props.option.value == 'In progress'"/>
                    <font-awesome-icon icon="check-circle" class="icon-success mr-2" v-if="props.option.value == 'Completed'"/>
                    <span class="ml-2">
                      {{ props.option.value }}
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <font-awesome-icon icon="exclamation-triangle" class="risk-0 mr-2" v-if="props.option.value == 'Not started'"/>
                    <font-awesome-icon icon="times-circle" class="icon-danger mr-2" v-if="props.option.value == 'Skipped'"/>
                    <font-awesome-icon icon="exclamation-triangle" class="risk-9 mr-2" v-if="props.option.value == 'In progress'"/>
                    <font-awesome-icon icon="check-circle" class="icon-success mr-2" v-if="props.option.value == 'Completed'"/>
                    <span class="ml-2">
                      {{ props.option.value }}
                    </span>
                  </template>
                </multiselect>
              </b-form-group>
            </div>
          </div>
          <div class="col-sm-8" style="max-height: 600px; overflow: auto;">
            <b-form-group class="checklist_step_modal">
              <img
                v-if="checklistItem.name == 'status_load_framework'"
                src="@/assets/checklists/framework/status_load_framework.gif">
              <img
                v-if="checklistItem.name == 'status_statement_of_applicability'"
                src="@/assets/checklists/framework/status_statement_of_applicability.gif">
              <img
                v-if="checklistItem.name == 'status_define_policies'"
                src="@/assets/checklists/framework/status_define_policies.gif">
              <img
                v-if="checklistItem.name == 'status_map_policies_to_framework_controls'"
                src="@/assets/checklists/framework/status_map_policies_to_framework_controls.gif">
              <img
                v-if="checklistItem.name == 'status_load_systems'"
                src="@/assets/checklists/framework/status_load_systems.gif">
              <img
                v-if="checklistItem.name == 'status_load_vulnerabilities'"
                src="@/assets/checklists/framework/status_load_vulnerabilities.gif">
              <img
                v-if="checklistItem.name == 'status_map_vulnerabilities_to_systems'"
                src="@/assets/checklists/framework/status_map_vulnerabilities_to_systems.gif">
              <img
                v-if="checklistItem.name == 'status_classify_systems'"
                src="@/assets/checklists/framework/status_classify_systems.gif">
              <img
                v-if="checklistItem.name == 'status_define_security_measures'"
                src="@/assets/checklists/framework/status_define_security_measures.gif">
              <img
                v-if="checklistItem.name == 'status_map_security_measures_to_framework_controls'"
                src="@/assets/checklists/framework/status_map_security_measures_to_framework_controls.gif">
              <img
                v-if="checklistItem.name == 'status_map_security_measures_to_systems'"
                src="@/assets/checklists/framework/status_map_security_measures_to_systems.gif">
              <img
                v-if="checklistItem.name == 'status_validate_security_measures_on_systems'"
                src="@/assets/checklists/framework/status_validate_security_measures_on_systems.gif">
              <img
                v-if="checklistItem.name == 'status_define_generic_controls'"
                src="@/assets/checklists/framework/status_define_generic_controls.gif">
              <img
                v-if="checklistItem.name == 'status_map_generic_controls_to_framework_controls'"
                src="@/assets/checklists/framework/status_map_generic_controls_to_framework_controls.gif">
              <img
                v-if="checklistItem.name == 'status_schedule_update_of_generic_controls'"
                src="@/assets/checklists/framework/status_schedule_update_of_generic_controls.gif">
              <img
                v-if="checklistItem.name == 'status_schedule_update_of_policies'"
                src="@/assets/checklists/framework/status_schedule_update_of_policies.gif">
              <img
                v-if="checklistItem.name == 'status_schedule_update_of_system_vulnerabilities'"
                src="@/assets/checklists/framework/status_schedule_update_of_system_vulnerabilities.gif">
              <img
                v-if="checklistItem.name == 'status_schedule_update_of_system_classifications'"
                src="@/assets/checklists/framework/status_schedule_update_of_system_classifications.gif">
              <img
                v-if="checklistItem.name == 'status_validate_implementation'"
                src="@/assets/checklists/framework/status_validate_implementation.gif">
            </b-form-group>
          </div>
        </div>
        <div class="proxy-footer-lg">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideChecklistItemModal"
                            qid="edit-checklist-item-cancel-button"
                  >
                    {{ $t('checklists.update.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            :disabled="buttonDisabled"
                            qid="edit-checklist-item-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('checklists.update.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete Framework confirm modal template -->
    <b-modal ref="delete-framework" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideConfirmDeleteModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" qid="delete-f-title">
            {{ $t('maintenance.frameworks.delete.title') }}
          </h5>
        </div>
      </template>
      <div class="mb-2 mt-3" style="font-size:18px;">
        <div>
          {{ $t('maintenance.frameworks.delete.description') }}
          <span class="text-bold">
            {{ itemToDelete.name }}
          </span>
        </div>
        <div class="mt-2">
          {{ $t('maintenance.frameworks.delete.are_you_sure') }}
        </div>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideConfirmDeleteModal"
                            qid="delete-f-cancel-button"
                  >
                    {{ $t('maintenance.frameworks.delete.cancel') }}
                  </b-button>
                  <b-button @click="confirmDeleteF(itemToDelete)"
                            class="ml-3"
                            variant="danger"
                            qid="delete-f-submit-button"
                            v-if="isGranted([permissions.FRAMEWORK_DELETE, permissions.FRAMEWORK_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
                  >
                    {{ $t('maintenance.frameworks.delete.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <ConfirmRemoveAttachment
            :object="selectedFile"
            :show="showDeleteAttachmentModal"
            type="file"
            @closeConfirmModal="closeDeleteAttachmentModal"
    ></ConfirmRemoveAttachment>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Avatar from '@/components/Avatar.vue';
import FrameworkControlsTable from '@/views/Frameworks/FrameworkControlsTable.vue';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import FrameworkHistory from '@/views/Frameworks/FrameworkHistory.vue';
import GuideButton from '@/components/guide/GuideButton.vue';


export default {
  name: 'FrameworkView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.title'),
          to: '/maintenance'
        },
        {
          text: this.$t('maintenance.frameworks.title'),
          to: '/maintenance/frameworks'
        }
      ],
      framework: {},
      showInput: false,
      showSingleLinkLabel: true,
      showFullDescription: false,
      descriptionLength: config.descriptionLength,
      inputLink: '',
      itemToDelete: {},
      fForm: {
        name: '',
        description: '',
        client:'',
      },
      cForm: {
        client:'',
      },
      error: '',
      timeLimitItems: [],
      timeLimitInput: '',
      buttonDisabled: false,
      selectedFile: {},
      selectedLink: {},
      showDeleteAttachmentModal: false,
      showDeleteLinkModal: false,
      showFullGuideDescription: false,
      wpGuide: {},
      myGuides: [],
      tabIndex: 0,
      frameworkTabClicked: false,
      showHistoryTab: false,

      checklistItem: {
        name: "",
        value: ""
      },
      checklistForm: {
        name: "",
        value: ""
      },
      checklistItemIndex: 0,
      checklistItems: [],
      statusItems: [
        { name: this.$t('checklists.status.not_started'), value: 'Not started' },
        { name: this.$t('checklists.status.skipped'), value: 'Skipped' },
        { name: this.$t('checklists.status.in_progress'), value: 'In progress' },
        { name: this.$t('checklists.status.completed'), value: 'Completed' }
      ],
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getWordpressLink() {
      return this.$store.state.wordpressLink
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList()
      } else {
        this.loadContent();
      }
    }
  },
  components: {
    Avatar,
    FrameworkControlsTable,
    ConfirmRemoveAttachment,
    FrameworkHistory,
    GuideButton
  },
  methods: {
    goToList() {
      this.$router.push('/maintenance/frameworks')
    },
    historyTabClicked() {
      this.showHistoryTab = true;
    },
    openDeleteAttachmentModal(object) {
      this.selectedFile = object
      this.showDeleteAttachmentModal = true
    },
    closeDeleteAttachmentModal () {
      this.selectedFile = {}
      this.showDeleteAttachmentModal = false;
      // Remove last item in breadcrumbs, because it will be added again on load content
      this.breadcrumbItems.pop();
      this.loadContent();
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    onFileChange(file) {
      let files = file.target.files;
      let self = this;
      let formData = new FormData();
      let sizeError = false;

      for (var i = 0; i < files.length; i++) {
        // Equal to 10 MB
        if (files[i].size > 10485760) {
          sizeError = true;
        }
        formData.append('document[]', files[i]);
      }

      formData.append("name", "documents");
      formData.append("model_type", this.framework.who_am_i);
      formData.append("model_id", this.framework.id);

      if (sizeError) {
        this.error = this.$t('system.file_size')
      } else {
        piincHttp.post('documents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function() {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'))
          self.error = ''
          self.loadContent()
        }, function(error) {
          self.error = error.data.message;
          if (error.data.errors['document.0']) {
            self.error += ' ' + error.data.errors['document.0'][0]
          }
        })
      }
    },
    resetFile() {
      let imagefile = document.querySelector('#file');
      imagefile.value = '';
    },
    openGuide() {
      if (this.wpGuide && this.wpGuide.link) {
        window.open(this.wpGuide.link, '_blank')
      }
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance' && object.enabled
      });
      return existingGuide
    },
    loadStepGuides(code) {
      let self = this;
      this.myGuides = []
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      let type = ''

      if (code == 'status_load_framework') {
        code = 'framework'
        type = 'Maintenance'
      }

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: type,
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
          piincHttp.get(self.getWordpressLink + self.myGuides[0].external_id).then(function(response) {
            self.wpGuide = response.data
          }, function() {});
        }
      }, function() {});
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Maintenance',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('frameworks/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.framework = response.data;

        self.checklistItems = [
          { name: 'status_load_framework', value: self.framework.status_load_framework },
          { name: 'status_statement_of_applicability', value: self.framework.status_statement_of_applicability },
          { name: 'status_define_policies', value: self.framework.status_define_policies },
          { name: 'status_map_policies_to_framework_controls', value: self.framework.status_map_policies_to_framework_controls },
          { name: 'status_load_systems', value: self.framework.status_load_systems },
          { name: 'status_load_vulnerabilities', value: self.framework.status_load_vulnerabilities },
          { name: 'status_map_vulnerabilities_to_systems', value: self.framework.status_map_vulnerabilities_to_systems },
          { name: 'status_classify_systems', value: self.framework.status_classify_systems },
          { name: 'status_define_security_measures', value: self.framework.status_define_security_measures },
          { name: 'status_map_security_measures_to_framework_controls', value: self.framework.status_map_security_measures_to_framework_controls },
          { name: 'status_map_security_measures_to_systems', value: self.framework.status_map_security_measures_to_systems },
          { name: 'status_validate_security_measures_on_systems', value: self.framework.status_validate_security_measures_on_systems },
          { name: 'status_define_generic_controls', value: self.framework.status_define_generic_controls },
          { name: 'status_map_generic_controls_to_framework_controls', value: self.framework.status_map_generic_controls_to_framework_controls },
          { name: 'status_schedule_update_of_generic_controls', value: self.framework.status_schedule_update_of_generic_controls },
          { name: 'status_schedule_update_of_policies', value: self.framework.status_schedule_update_of_policies },
          { name: 'status_schedule_update_of_system_vulnerabilities', value: self.framework.status_schedule_update_of_system_vulnerabilities },
          { name: 'status_schedule_update_of_system_classifications', value: self.framework.status_schedule_update_of_system_classifications },
          { name: 'status_validate_implementation', value: self.framework.status_validate_implementation }
        ]
        // replace null and blank values with "Not started"
        for (var i = 0; i < self.checklistItems.length; i++) {
          if (self.checklistItems[i].value === null || self.checklistItems[i].value === ' ' || self.checklistItems[i].value === '') {
              self.checklistItems[i].value = 'Not started';
          }
        }

        // Push content name in breadcrumb aFrameworkay
        self.breadcrumbItems.push({text: self.framework.name, active: true});
      }, function() {});
    },
    editChecklistSubmit(selectedStatus) {
      event.preventDefault()
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      this.cForm.client = this.getClient.slug;
      this.cForm.slug = this.framework.slug
      this.cForm[this.checklistItem.name] = selectedStatus.value;

      piincHttp.put('frameworks/' + this.cForm.slug, this.cForm).then(function(response) {
        self.hideChecklistItemModal();
        self.buttonDisabled = false;

        self.breadcrumbItems.pop();
        self.loadContent();

        self.$toastr('success', self.$t('maintenance.frameworks.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    showChecklistItemModal(object, index) {
      let self = this;

      this.loadStepGuides(object.name)
      this.checklistItem = object
      this.checklistForm = object
      this.checklistItemIndex = index
      this.$refs['edit-checklist-item'].show()
    },
    hideChecklistItemModal() {
      let self = this;

      this.checklistItem = {}
      this.$refs['edit-checklist-item'].hide()
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-framework'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-framework'].hide()
    },
    confirmDeleteF(object) {
      let self = this;
      piincHttp.delete('frameworks/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.frameworks.delete.success'))
      }, function() {});
    },
    showEditFrameworkModal(object) {
      let self = this;
      this.error = ''

      this.fForm.slug = object.slug;
      this.fForm.name = object.name;
      this.fForm.description = object.description;

      this.$refs['edit-framework'].show()
    },
    hideEditFModal() {
      this.timeLimitInput = ''
      this.$refs['edit-framework'].hide()
    },
    editFFormSubmit(evt) {
      this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.fForm.client = this.getClient.slug;

      piincHttp.put('frameworks/' + this.fForm.slug, this.fForm).then(function(response) {
        self.hideEditFModal();
        self.buttonDisabled = false;
        // If slug is not changed, just reload content
        if (response.data.slug === self.framework.slug) {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.loadContent();
        } else {
          // If slug changed, reload state with new param
          self.$router.push({ name: 'maintenance.frameworks.view', params: { slug: response.data.slug }})
        }
        self.$toastr('success', self.$t('maintenance.frameworks.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    }
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
    }
    this.loadGuides('framework');
  }
}
</script>
